import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { UserAdminPermissionsDto } from '../../../../dtos/Users/UserAdminPermissionsDto';
import Section from '../../../Common/Section';

const PermissionLevels = {
    None: 0,
    ViewOnly: 1,
    Report: 3,
    All: 2,
};

const PERMISSION_LEVEL_KEYS = {
    [PermissionLevels.None]: 'userPermission.level.none',
    [PermissionLevels.ViewOnly]: 'userPermission.level.viewOnly',
    [PermissionLevels.Report]: 'userPermission.level.report',
    [PermissionLevels.All]: 'userPermission.level.all',
};

export default function UserPermissionSettings (props) {
    const { userAdminPermissions, setUserAdminPermissions } = props;
    const { t } = useTranslation();

    const [riskPermission, setRiskPermission] = useState('2');

    const handleFieldChange = (field, value) => {
        const updatedSettings = { ...userAdminPermissions };
        updatedSettings[field] = value;
        setUserAdminPermissions(updatedSettings);
    };

    const columns = useMemo(() => [
        {
            field: 'puProjectName',
            headerName: t('userPermission.column.locationProject'),
            flex: 1,
        },
        {
            field: 'puPermissionLevel',
            headerName: t('userPermission.column.permissions'),
            type: 'singleSelect',
            editable: true,
            flex: 1,
            valueOptions: [
                { value: PermissionLevels.None, label: t('userPermission.level.none') },
                { value: PermissionLevels.ViewOnly, label: t('userPermission.level.viewOnly') },
                { value: PermissionLevels.Report, label: t('userPermission.level.report') },
                { value: PermissionLevels.All, label: t('userPermission.level.all') },
            ],
            renderCell: (params) => {
                const key = PERMISSION_LEVEL_KEYS[params.row.puPermissionLevel];
                return key ? t(key) : '';
            },
        },
    ], [t]);

    function setRiskManagementValues (value) {
        const eventValue = parseInt(value);

        const updatedSettings = { ...userAdminPermissions };
        if (eventValue === 0) {
            updatedSettings.usViewRiskManagement = true;
            updatedSettings.usModifyRiskManagement = false;
        } else if (eventValue === 1) {
            updatedSettings.usViewRiskManagement = true;
            updatedSettings.usModifyRiskManagement = true;
        } else {
            updatedSettings.usViewRiskManagement = false;
            updatedSettings.usModifyRiskManagement = false;
        }
        setUserAdminPermissions(updatedSettings);
    }

    useEffect(() => {
        if (userAdminPermissions.usViewRiskManagement && userAdminPermissions.usModifyRiskManagement) {
            setRiskPermission('1');
        } else if (userAdminPermissions.usViewRiskManagement && !userAdminPermissions.usModifyRiskManagement) {
            setRiskPermission('0');
        } else {
            setRiskPermission('2');
        }
    }, [userAdminPermissions]);

    return (
        <Box sx={{ pt: 1 }}>
            <Section title={t('userPermission.section.riskManagementModule')}>
                <Grid item xs={12}>
                    <RadioGroup
                        row
                        name='risks-permissions-group'
                        onChange={(event) => setRiskManagementValues(event.target.value)}
                        value={riskPermission}
                    >
                        <FormControlLabel value={'0'} control={<Radio />} label={t('userPermission.option.view')} />
                        <FormControlLabel value={'1'} control={<Radio />} label={t('userPermission.option.modify')} />
                        <FormControlLabel value={'2'} control={<Radio />} label={t('userPermission.option.none')} />
                    </RadioGroup>
                </Grid>
            </Section>

            <Section title={t('userPermission.section.projectPermissions')}>
                <Grid item xs={12}>
                    <Box sx={{ height: '50vh', width: '100%' }}>
                        <DataGridPro
                            columns={columns}
                            rows={userAdminPermissions.projectUserXRefs ?? []}
                            getRowId={(row) => row.puId}
                            pagination
                            getRowHeight={() => 'auto'}
                            onCellEditCommit={(params, event) => {
                                const editedProjectUserXRefs = JSON.parse(JSON.stringify(userAdminPermissions.projectUserXRefs));
                                editedProjectUserXRefs.find(x => x.puId === params.id).puPermissionLevel = event.target.value;
                                handleFieldChange('projectUserXRefs', editedProjectUserXRefs);
                            }}
                            initialState={{
                                sorting: {
                                    sortModel: [{ field: 'puProjectName', sort: 'asc' }],
                                },
                            }}
                        />
                    </Box>
                </Grid>
            </Section>
        </Box>
    );
}

UserPermissionSettings.propTypes = {
    userAdminPermissions: PropTypes.objectOf(UserAdminPermissionsDto),
    setUserAdminPermissions: PropTypes.func.isRequired,
};
